import { useEffect, useMemo, useState } from 'react';
import { ApiStatisticsFilters, ApiStatisticsPreset, ApiStatisticsProductTypeOption } from '../../../types';

export type ProductTypeId = ApiStatisticsProductTypeOption['id'];

export type StatisticsRequestParams = {
	startDate: string;
	endDate: string;
	productType: ProductTypeId;
	includeVat: boolean;
};

export type StatisticsSectionFilters = {
	isReady: boolean;
	selectedPeriod: string | null;
	periodStartDate: string;
	periodEndDate: string;
	selectedProductType: ProductTypeId;
	requestParams: StatisticsRequestParams;
	selectedPeriodLabel: string;
	setSelectedPeriod: (value: string | null) => void;
	setPeriodStartDate: (value: string) => void;
	setPeriodEndDate: (value: string) => void;
	setSelectedProductType: (value: ProductTypeId) => void;
};

export function clampDate(value: string, minDate: string, maxDate: string): string {
	if (value < minDate) {
		return minDate;
	}

	if (value > maxDate) {
		return maxDate;
	}

	return value;
}

export function getPresetForDateRange(
	presets: ApiStatisticsPreset[],
	startDate: string,
	endDate: string
): ApiStatisticsPreset | null {
	return presets.find((preset) => preset.startDate === startDate && preset.endDate === endDate) ?? null;
}

export function getSelectedPeriodLabel(
	presets: ApiStatisticsPreset[],
	selectedPeriod: string | null,
	periodStartDate: string,
	periodEndDate: string,
	rangeSeparator: string
): string {
	const selectedPeriodPreset = presets.find((preset) => preset.id === selectedPeriod) ?? null;

	return selectedPeriodPreset?.label ?? `${periodStartDate} ${rangeSeparator} ${periodEndDate}`;
}

export function getDefaultProductType(productTypes: ApiStatisticsProductTypeOption[]): ProductTypeId {
	return productTypes.find((productType) => productType.id === 'all')?.id ?? productTypes[0]?.id ?? 'all';
}

export function useStatisticsSectionFilters(
	filters: ApiStatisticsFilters,
	includeVat: boolean,
	rangeSeparator: string
): StatisticsSectionFilters {
	const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
	const [periodStartDate, setPeriodStartDate] = useState('');
	const [periodEndDate, setPeriodEndDate] = useState('');
	const [selectedProductType, setSelectedProductType] = useState<ProductTypeId>('all');

	useEffect(() => {
		const defaultPreset =
			filters.presets.find((preset) => preset.id === filters.defaultPresetId) ?? filters.presets[0];

		if (!defaultPreset) {
			return;
		}

		setSelectedPeriod(defaultPreset.id);
		setPeriodStartDate(defaultPreset.startDate);
		setPeriodEndDate(defaultPreset.endDate);
		setSelectedProductType(getDefaultProductType(filters.productTypes));
	}, [filters]);

	const selectedPeriodLabel = useMemo(
		() => getSelectedPeriodLabel(filters.presets, selectedPeriod, periodStartDate, periodEndDate, rangeSeparator),
		[filters.presets, selectedPeriod, periodStartDate, periodEndDate, rangeSeparator]
	);

	return {
		isReady: periodStartDate !== '' && periodEndDate !== '',
		selectedPeriod,
		periodStartDate,
		periodEndDate,
		selectedProductType,
		requestParams: {
			startDate: periodStartDate,
			endDate: periodEndDate,
			productType: selectedProductType,
			includeVat,
		},
		selectedPeriodLabel,
		setSelectedPeriod,
		setPeriodStartDate,
		setPeriodEndDate,
		setSelectedProductType,
	};
}
